import {createAuthClient} from "better-auth/react";
import {
  magicLinkClient,
  organizationClient,
  adminClient,
} from "better-auth/client/plugins";
import {
  ac,
  admin,
  getClientEnv,
  isDevAuthBypass,
  DEV_STUB_SESSION,
} from "@nosible/shared/lib";

/**
 * Better Auth client for the React application
 *
 * Provides authentication methods and hooks:
 * - useSession() - Get current session state
 * - signIn.email() - Sign in with email/password
 * - signIn.magicLink() - Send magic link
 * - signUp.email() - Create new account
 * - signOut() - Sign out
 *
 * Organization methods:
 * - organization.create() - Create new organization
 * - organization.list() - List user's organizations
 * - organization.setActive() - Set active organization
 * - organization.inviteMember() - Invite member to organization
 *
 * Admin methods:
 * - admin.hasPermission() - Check if user has permission
 * - admin.listUsers() - List all users (admin only)
 * - admin.setRole() - Set user role (admin only)
 */
export const authClient = createAuthClient({
  baseURL: getClientEnv()?.API_BASE_URL!,
  basePath: "/auth",
  fetchOptions: {
    credentials: "include",
  },
  plugins: [
    magicLinkClient(),
    organizationClient(),
    adminClient({
      ac,
      roles: {
        admin,
      },
    }),
  ],
});

const {useSession: realUseSession, signIn, signUp, signOut} = authClient;

// DEV-ONLY: when PUBLIC_DEV_AUTH_BYPASS=1, return a stub session so the admin
// shell renders for local UI review without a real Better Auth login. The flag
// is build-time injected (see admin/build.ts) and stripped from production
// builds. Other authClient methods (signIn/signOut) are NOT stubbed — calling
// them with bypass on will fail at runtime, which is fine: we only want the UI.
const useStubSession = () =>
  ({
    data: DEV_STUB_SESSION,
    isPending: false,
    error: null,
    refetch: () => Promise.resolve({data: DEV_STUB_SESSION, error: null}),
  }) as unknown as ReturnType<typeof realUseSession>;

const useSession = isDevAuthBypass() ? useStubSession : realUseSession;

export {useSession, signIn, signUp, signOut};
